/**
 * TacticalMapPanel - top-down view of the combat grid, with click-to-move.
 *
 * Distinct from MapPanel.tsx, which is the world map. This one shows where
 * everyone is standing right now.
 *
 * Square grid, not hex, and that is a rules decision rather than an aesthetic
 * one. SRD 5.2 combat says a square of Difficult Terrain "costs 2 squares to
 * enter", and that it costs 1 square to enter a square "orthogonally or
 * diagonally adjacent" (combat.md:67). Diagonal equals 5ft, 8 neighbours, which
 * is exactly what `Position::distance_to` computes. A hex board would render 6
 * neighbours over 8-neighbour rules and misreport every diagonal.
 *
 * Clicking a square STAGES a move; it does not send one. ChatPanel submits the
 * staged path as a `player_move` alongside whatever the player typed, so the
 * engine's collector folds both into one 5E turn through the disjoint movement
 * slot: move and attack in the same round, or move alone on an empty input.
 * The engine owns legality (budget, difficult terrain, opportunity attacks);
 * this panel only proposes a destination. Shares `stagedMove` with the 3D
 * board so the two surfaces submit through one path.
 *
 * Works from day one with no authored scene geometry: the grid auto-sizes to the
 * occupied cells plus a one-cell margin and draws plain cells with no walls. It
 * gains walls, terrain, and cover shading as scene authoring lands rather than
 * staying hidden until then.
 */

import { useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ChevronDown, ChevronUp, Grid3x3 } from 'lucide-react';
import { useGameStore } from '@/lib/stores/gameStore';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { deriveBounds, describeMap, tokenLabel, tokensFrom } from './tactical-map-helpers';
import type { MapBounds, Token, TokenTone } from './tactical-map-helpers';

/** Smallest a cell may render before the board starts scrolling instead. */
const CELL_MIN_PX = 26;
/** Matches the `border` utility on the board. */
const BOARD_BORDER_PX = 1;

/**
 * Same hues the scene panel puts on its attitude badges
 * (`SceneCharsSection.getAttitudeBadge`), so a creature reads the same wherever
 * you meet it. A shade lighter than those badges, because a ring is a thin
 * outline rather than a filled pill.
 *
 * `unfriendly` used to share the neutral ring, which made someone who dislikes
 * you look exactly like a merchant.
 */
const RING_BY_TONE: Record<TokenTone, string> = {
  party: 'ring-emerald-400',
  hostile: 'ring-red-500',
  unfriendly: 'ring-orange-500',
  friendly: 'ring-green-500',
  allied: 'ring-blue-500',
  neutral: 'ring-slate-400',
};

export default function TacticalMapPanel() {
  const { partyMembers, sceneCharacters, playerCharacterId, currentTurn, encounterType } =
    useGameStore();
  const stagedMove = useGameStore((s) => s.stagedMove);
  const setStagedMove = useGameStore((s) => s.setStagedMove);
  const [isExpanded, toggleExpanded] = usePanelCollapse('tactical-map');
  const boardRef = useRef<HTMLDivElement | null>(null);

  const tokens = useMemo(
    () => tokensFrom([...partyMembers, ...sceneCharacters], playerCharacterId, currentTurn),
    [partyMembers, sceneCharacters, playerCharacterId, currentTurn]
  );

  const bounds = useMemo(() => deriveBounds(tokens), [tokens]);

  const self = useMemo(() => tokens.find((t) => t.isPlayer), [tokens]);

  /**
   * Stage the clicked square.
   *
   * The cells are painted with gradients rather than rendered as elements (see
   * the board comment below), so there is nothing per-cell to hang a handler
   * on. One listener on the board plus the measured rect gives the same result
   * for zero extra nodes, which is the whole point of painting them.
   */
  const stageCellAt = useCallback(
    (clientX: number, clientY: number) => {
      const board = boardRef.current;
      if (!board || !bounds || !self || self.isDown) return;
      // Mid-playback the board is still catching up to the round that just
      // resolved, so a click would stage a square against positions the player
      // can no longer see. ChatPanel refuses to send during playback anyway.
      if (useGameStore.getState().presentationPlaybackActive) return;
      const rect = board.getBoundingClientRect();
      const cellW = rect.width / bounds.width;
      const cellH = rect.height / bounds.height;
      if (cellW <= 0 || cellH <= 0) return;

      const col = Math.floor((clientX - rect.left) / cellW);
      const row = Math.floor((clientY - rect.top) / cellH);
      if (col < 0 || row < 0 || col >= bounds.width || row >= bounds.height) return;

      const x = col + bounds.minX;
      const y = row + bounds.minY;
      // Clicking your own square clears a staged move rather than staging a
      // zero-distance one.
      if (x === self.x && y === self.y) {
        setStagedMove(null);
        return;
      }
      // Chebyshev x 5ft: the same 8-neighbour diagonal rule the board renders
      // and `Position::distance_to` computes.
      const distanceFt = Math.max(Math.abs(x - self.x), Math.abs(y - self.y)) * 5;
      setStagedMove({ path: [{ x, y, z: 0 }], distanceFt });
    },
    [bounds, self, setStagedMove]
  );

  // The board is one image to a screen reader (see the role note below), so the
  // roster has to live in its label: the per-token labels inside are ignored.
  const mapLabel = useMemo(() => describeMap(tokens), [tokens]);

  // Combat-only: outside an encounter there are no grid positions to show, and
  // the engine clears them on combat exit anyway.
  if (encounterType !== 'combat') return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Grid3x3 className="h-4 w-4" />
            Battlefield
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse tactical map' : 'Expand tactical map'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="p-3 pt-0">
          {bounds === null ? (
            <p className="text-xs text-muted-foreground py-4 text-center">
              No positions reported for this encounter yet.
            </p>
          ) : (
            <>
              {/* Wide grids scroll inside their own container so the panel
                  never forces the page to scroll horizontally. */}
              <div className="overflow-x-auto">
                {/* role="img", not role="grid": ARIA requires a grid to own
                    rows and a gridcell to sit inside one, and this board places
                    tokens directly with gridColumn/gridRow so there are no row
                    elements to own them. The panel is read-only with no cell
                    navigation, so one labelled image is the honest markup. */}
                {/* The cells are painted, not built. Rendering one div per cell
                    cost width × height nodes, so it grew with the square of how
                    far apart the fight spread: a chase that drifts 60 squares
                    across is 3,600 nodes rebuilt on every round result. Two
                    repeating gradients draw the same lines for zero nodes, and
                    sizing them off the track count keeps them aligned while the
                    tracks stretch. No authored walls or terrain yet, so every
                    cell looks the same; shading arrives with scene geometry.

                    `minWidth` carries the border because preflight makes this
                    border-box: the gradients are measured against the padding
                    box, so without those 2px the painted lines would run at a
                    slightly tighter pitch than the tracks and drift out of true
                    across the board. Only bites once minWidth binds, which is
                    exactly the wide, spread-out fight this all exists for. */}
                <div
                  ref={boardRef}
                  role="img"
                  aria-label={mapLabel}
                  onClick={(e) => stageCellAt(e.clientX, e.clientY)}
                  // The border closes the frame: each gradient draws a line at
                  // its cell's leading edge, so the far right and bottom would
                  // otherwise run to nothing.
                  className={`relative grid bg-muted/30 rounded-md mx-auto border border-border/40 ${
                    self && !self.isDown ? 'cursor-pointer' : ''
                  }`}
                  style={{
                    gridTemplateColumns: `repeat(${bounds.width}, minmax(${CELL_MIN_PX}px, 1fr))`,
                    gridTemplateRows: `repeat(${bounds.height}, minmax(${CELL_MIN_PX}px, 1fr))`,
                    minWidth: `${bounds.width * CELL_MIN_PX + 2 * BOARD_BORDER_PX}px`,
                    backgroundImage: `linear-gradient(to right, hsl(var(--border) / 0.4) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.4) 1px, transparent 1px)`,
                    backgroundSize: `calc(100% / ${bounds.width}) calc(100% / ${bounds.height})`,
                  }}
                >
                  {stagedMove?.path.length ? (
                    <StagedCell
                      cell={stagedMove.path[stagedMove.path.length - 1]!}
                      bounds={bounds}
                    />
                  ) : null}
                  {tokens.map((t) => (
                    <TokenCell key={t.charId} token={t} bounds={bounds} />
                  ))}
                </div>
              </div>
              {self && !self.isDown && (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {stagedMove
                    ? `Move staged (${stagedMove.distanceFt} ft) — sends with your next action`
                    : 'Click a square to stage a move'}
                </p>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * The square a click staged. Sits under the tokens (they render after it) so a
 * creature standing there is never obscured by the marker.
 */
function StagedCell({ cell, bounds }: { cell: { x: number; y: number }; bounds: MapBounds }) {
  return (
    <div
      aria-hidden
      className="rounded-sm ring-2 ring-sky-400 bg-sky-400/20 pointer-events-none"
      style={{
        gridColumn: `${cell.x - bounds.minX + 1} / span 1`,
        gridRow: `${cell.y - bounds.minY + 1} / span 1`,
      }}
    />
  );
}

function TokenCell({ token, bounds }: { token: Token; bounds: MapBounds }) {
  // CSS grid lines are 1-indexed, hence the +1 on top of the bounds offset.
  const colStart = token.x - bounds.minX + 1;
  const rowStart = token.y - bounds.minY + 1;

  const ring = RING_BY_TONE[token.tone];

  const label = tokenLabel(token);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center justify-center p-0.5"
            style={{
              gridColumn: `${colStart} / span ${token.footprint}`,
              gridRow: `${rowStart} / span ${token.footprint}`,
            }}
          >
            <Avatar
              className={`h-full w-full aspect-square ring-2 ${ring} ${
                token.isCurrentTurn ? 'ring-offset-2 ring-offset-background' : ''
              } ${token.isDown ? 'opacity-40 grayscale' : ''} ${
                token.isPlayer ? 'shadow-[0_0_0_1px_currentColor]' : ''
              }`}
            >
              {token.avatarUrl && <AvatarImage src={token.avatarUrl} alt={token.name} />}
              <AvatarFallback className="text-[10px] font-semibold">
                {token.avatarEmoji || token.name[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div>{label}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
